import invariant from 'invariant';
import queryString from 'query-string';
import deepEqual from 'react-fast-compare';

import { configValue } from 'utils/config';
import { filterNulls, objectEmpty, updateObject } from 'utils/functional';
import { RouteComponentProps } from 'utils/withRouter';

import { log } from './logging';

export const withParams = (
  l: RouteComponentProps['location'],
  params: Record<string, string>,
) => ({
  ...l,
  search: queryString.stringify(
    updateObject(queryString.parse(l.search), params),
  ),
});
export const searchEqual = (l1: Location, l2: Location) =>
  deepEqual(queryString.parse(l1.search), queryString.parse(l2.search));

const trimTrailingSlash = (s: string) => s.replace(/\/$/, '');

export const pathnameEqual = (p1: string, p2: string) =>
  trimTrailingSlash(p1) === trimTrailingSlash(p2);
export const pathWithParams = (
  path: string | null | undefined,
  params: Readonly<Record<string, string | null | undefined>>,
) => {
  if (!path) return '';
  if (objectEmpty(params)) return path;
  let splitPath;
  let hash = '';

  if (path.includes('#')) {
    splitPath = path.split('#');
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line prefer-destructuring
    hash = splitPath[1];
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line prefer-destructuring, no-param-reassign
    path = splitPath[0];
  }

  splitPath = path.split('?');
  const trimmedPath = trimTrailingSlash(splitPath[0]);
  const search = splitPath[1];
  return `${trimmedPath}?${queryString.stringify({
    ...filterNulls(params),
    ...queryString.parse(search),
  })}${hash ? `#${hash}` : ''}`;
};
export const hasParam = (search: string, param: string) =>
  param in queryString.parse(search);

export const getParam = (
  l: Location | RouteComponentProps['location'],
  param: string,
) => {
  const q = queryString.parse(l.search)[param];
  return q;
};
export const getFirstParam = (
  l: Location | RouteComponentProps['location'],
  param: string,
) => {
  const q = queryString.parse(l.search)[param];
  return Array.isArray(q) ? q[0] : q;
};

export const getQueryParamStringArray = (
  search: string,
  param: string,
): string[] => {
  const parsed = queryString.parse(search);
  const parsedParam = parsed[param];
  if (typeof parsedParam === 'string') {
    return [parsedParam];
  }
  if (typeof parsedParam === 'undefined' || parsedParam === null) {
    return [];
  }
  if (Array.isArray(parsedParam)) {
    return parsedParam.filter((value) => value !== null) as string[];
  }
  // Sanity check, should never happen
  log.error(
    'parsedParam was expected to be an Array at that point. ' +
      'Actual value was ',
    JSON.stringify(parsedParam),
  );
  return parsedParam as string[];
};
export const removeParam = (qs: string, param: string) =>
  qs
    .split('&')
    .filter(
      (queryParam) =>
        !(queryParam === param || queryParam.startsWith(`${param}=`)),
    )
    .join('&');
export const isUrlWithProtocol = (url: string) => /^https?:\/\//.test(url);
export const hasUTMParam = (url: string) =>
  Object.keys(queryString.parse(url.split('?')[1])).filter((p) =>
    p.startsWith('utm_'),
  ).length > 0;
export const isExternalURL = (url: string) => {
  if (!url) return;

  // ignore mailto
  if (url.startsWith('mailto:')) {
    return false;
  }

  // ignore links that are relative to the current url, like anchors, query params
  if (url.startsWith('#') || url.startsWith('?')) {
    return false;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const domain = (u: any) =>
    u.replace('http://', '').replace('https://', '').split('/')[0];

  const appUrl = configValue('idealist', 'appUrl') || '';
  return Boolean(domain(url)) && domain(url) !== domain(appUrl);
};
export const getRelativeURL = (url: string) => {
  invariant(typeof url === 'string', 'url should be a string');
  if (url.startsWith('mailto:') || url.startsWith('#') || url.startsWith('?'))
    return url;

  if (isUrlWithProtocol(url)) {
    const parsedUrl = new URL(url);
    return parsedUrl.toString().substring(parsedUrl.origin.length) || '/';
  }

  return url.replace(/^(?:\/\/|[^/]+)*/, '');
};
